import React from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { STATE_RING } from "../../lib/state-ring.js";
import { cn } from "../../lib/utils.js";
import { selectTriggerLook } from "../../lib/field-frame.js";
import { useFormField } from "../../lib/form-field-context.js";
import { useOverlayPresence } from "../../primitives/OverlaySurface.js";
import {
  getLayerRoot,
  useDismissable,
  useLayer,
  usePosition,
  useRovingTabIndex,
} from "../../primitives/index.js";

/* ---------------- Select ----------------
   Engine-backed (strategy §14.4 increment 5.2): layer ordering, dismissal,
   placement, and arrow-key semantics all come from ../primitives. Focus
   stays on the trigger; the listbox tracks the active option via ids +
   aria-activedescendant (full combobox-role conversion is owned by the §2.4
   Listbox/Combobox rebuild). */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  label?: string;
  ariaLabel?: string;
  /** Low-chrome, content-sized trigger for choices beside a primary value. */
  variant?: "default" | "inline";
  /** `sm` is the 28px row, `md` the 36px row; `inline` is always 28px. */
  size?: "sm" | "md";
  hint?: string;
  options?: SelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  // `id` and the two `aria-*` below carry `| undefined` because the repo
  // compiles with `exactOptionalPropertyTypes`: a caller holding an optional
  // id cannot otherwise hand it over without a conditional spread.
  id?: string | undefined;
  className?: string;
  name?: string;
  /**
   * A description held OUTSIDE this control — the same prop `Input` already
   * forwards. A caller that owns the field's error (a VSM fact row, say)
   * renders the message itself, so the trigger has to point at it; joined
   * with the enclosing FormField's own ids rather than replacing them.
   */
  "aria-describedby"?: string | undefined;
  /**
   * An invalid state the CALLER holds, as `Input` accepts it — but it paints
   * the border ONLY. `aria-invalid` is not among the states ARIA allows on
   * `role="button"`, and this trigger is a button until the §2.4
   * Listbox/Combobox rebuild; `FormField` reaches the same conclusion for the
   * error it owns (see `FormField.test.tsx`). The description above is what
   * carries the reason to a reader.
   */
  "aria-invalid"?: boolean | "true" | "false" | undefined;
  /**
   * Focus handlers on the TRIGGER, forwarded because a select is an editing
   * control like any other.
   *
   * Added for PB-4923's field editing lease (AC5). It is tempting to argue a
   * dropdown needs none — it commits instantly, so there is no half-typed
   * draft to lose — but that misses the window the lease is actually for: the
   * person has the list open and is choosing, and an approved Player AI change
   * landing on that same field in between would be silently overwritten by
   * their pick, with nothing on screen saying either happened.
   *
   * Deliberately NOT `onBlur` on the listbox: the trigger keeps DOM focus while
   * the popover is open, so one pair of handlers covers the whole interaction.
   */
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
}

export function Select({
  onFocus,
  onBlur,
  label,
  ariaLabel,
  variant = "default",
  size = "md",
  hint,
  options = [],
  value,
  defaultValue,
  onChange,
  placeholder = "Select…",
  disabled,
  id,
  className,
  name,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: SelectProps) {
  const autoId = React.useId();
  const selId = id ?? autoId;
  const field = useFormField();
  // FormField CLONES its child with the very ids it also puts on the context,
  // so a Select inside one now receives the same string twice. Join through a
  // set: a caller's own ids are added, an echo of the field's are not.
  const describedBy =
    [
      ...new Set(
        `${ariaDescribedBy ?? ""} ${field?.describedBy ?? ""}`.split(" "),
      ),
    ]
      .filter(Boolean)
      .join(" ") || undefined;
  const callerInvalid = ariaInvalid === true || ariaInvalid === "true";
  const optId = (i: number) => `${selId}-opt-${i}`;
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState<string | undefined>(
    defaultValue,
  );
  const current = isControlled ? value : internal;

  const [open, setOpen] = React.useState(false);
  const layerId = useLayer({ open });

  const {
    activeIndex,
    setActiveIndex,
    onKeyDown: rovingKeyDown,
  } = useRovingTabIndex({
    itemCount: options.length,
    initialIndex: Math.max(
      0,
      options.findIndex((o) => o.value === current),
    ),
  });

  const wrapRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  /* The listbox used to be destroyed in the same frame `open` flipped, so no
     CSS could give it an exit (ADR-0091). `present` keeps it mounted through
     that exit and `motionProps` makes it inert and aria-hidden while it runs.
     Everything that COMMITS, listens or registers still follows `open` — the
     logical state — so a listbox on its way out neither answers the keyboard
     nor swallows the click meant for whatever is behind it. */
  const { present, closing, motionProps } = useOverlayPresence(open, {
    ref: listRef,
  });

  const onDismiss = React.useCallback(() => setOpen(false), []);
  const insideRefs = React.useMemo(() => [wrapRef, listRef], []);
  useDismissable({ layerId, open, onDismiss, insideRefs });

  const pos = usePosition({
    // `present`, not `open`: measuring against a trigger the listbox has
    // already logically left drops the placement and flies it to the corner
    // halfway through its fade.
    open: present,
    triggerRef,
    contentRef: listRef,
    align: "start",
    sideOffset: 4,
    matchWidth: true,
  });

  React.useEffect(() => {
    if (open) {
      const i = options.findIndex((o) => o.value === current);
      setActiveIndex(i >= 0 ? i : 0);
    }
    // Snap the active option to the selection at open time only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelectorAll("li")[activeIndex] as
      | HTMLLIElement
      | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  const commit = (v: string) => {
    if (closing) return;
    if (options.find((option) => option.value === v)?.disabled) return;
    if (!isControlled) setInternal(v);
    onChange?.(v);
    setOpen(false);
  };

  const handleTriggerKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (
      e.key === "ArrowDown" ||
      e.key === "ArrowUp" ||
      e.key === "Enter" ||
      e.key === " "
    ) {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
    }
    if (!open) return;
    if (rovingKeyDown(e)) return;
    if (e.key === "Enter") {
      const opt = options[activeIndex];
      if (opt) commit(opt.value);
    }
  };

  const currentLabel = options.find((o) => o.value === current)?.label;
  const inline = variant === "inline";
  const look = selectTriggerLook(variant, size);

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <label
          htmlFor={selId}
          className="block text-xs font-medium text-pb-text-secondary mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative" ref={wrapRef}>
        <button
          type="button"
          id={selId}
          ref={triggerRef}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-describedby={describedBy}
          onClick={() => !disabled && setOpen((v) => !v)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={handleTriggerKey}
          className={cn(
            // The interaction grades, not hand-spelled states (ADR-0092):
            // `hover-quiet` carries the hover paint AND the transition, `press`
            // the active nudge that `active:bg-*` used to fake, and
            // `focus-frame` the text-field focus look (d8), which also holds
            // while the list is open (`aria-expanded`). No other focus class
            // sits beside it: a variant class would win on equal specificity.
            // The inline hover border stays off a focused or open trigger,
            // so it keeps the teal edge under the pointer.
            "w-full flex items-center justify-between gap-2 rounded-md border text-start hover-quiet press",
            look.box,
            field?.invalid || callerInvalid
              ? "border-pb-red/60 focus-frame-invalid"
              : inline
                ? "border-transparent not-aria-expanded:not-focus-visible:hover:border-pb-control-border focus-frame"
                : "border-pb-control-border focus-frame",
            disabled && "opacity-60 cursor-not-allowed",
          )}
        >
          <span
            className={cn(
              "truncate",
              currentLabel ? "text-pb-text" : "text-pb-text-muted",
            )}
          >
            {currentLabel ?? placeholder}
          </span>
          <svg className={look.chevron} viewBox="0 0 12 12" fill="none">
            <path
              d="M3 4.5L6 7.5L9 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        {name && <input type="hidden" name={name} value={current ?? ""} />}
      </div>
      {present &&
        typeof document !== "undefined" &&
        createPortal(
          <ul
            ref={listRef}
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={
              options.length ? optId(activeIndex) : undefined
            }
            {...motionProps}
            style={{
              position: "fixed",
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              minWidth: pos?.width ?? undefined,
              maxWidth: Math.max(pos?.width ?? 0, 320),
              // The engine sizes the panel from the content's scrollHeight and
              // returns a maxHeight only when it must clamp to the viewport. Apply
              // it (and scroll) here — a hardcoded max-h would desync the rendered
              // height from the placement and float the panel off its trigger.
              maxHeight: pos?.maxHeight,
              overflowY: pos?.maxHeight ? "auto" : undefined,
              visibility: pos ? "visible" : "hidden",
            }}
            className={cn(
              motionProps.className,
              "z-popover border border-pb-control-border shadow-lg",
              inline
                ? "min-w-36 rounded-lg bg-pb-card-elevated p-1.5"
                : "rounded-md bg-pb-card py-1",
            )}
          >
            {options.length === 0 ? (
              <li className="px-3 py-2 text-sm text-pb-text-muted">
                No options
              </li>
            ) : (
              options.map((o, i) => {
                const selected = o.value === current;
                const active = i === activeIndex;
                return (
                  <li
                    key={o.value}
                    id={optId(i)}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={o.disabled || undefined}
                    onMouseEnter={() => setActiveIndex(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (!o.disabled) commit(o.value);
                    }}
                    className={cn(
                      "flex items-center justify-between gap-2 py-1.5 transition-colors",
                      inline
                        ? "rounded-md px-2.5 text-body-small"
                        : "px-3 text-sm",
                      o.disabled
                        ? "cursor-not-allowed text-pb-text-muted opacity-60"
                        : "cursor-pointer",
                      active && !o.disabled
                        ? inline
                          ? `bg-pb-teal/10 ${STATE_RING.teal}`
                          : `pb-overlay-paint ${STATE_RING.neutral}`
                        : "",
                      selected && !o.disabled
                        ? "text-pb-teal-text"
                        : !o.disabled
                          ? "text-pb-text"
                          : "",
                    )}
                  >
                    <span className="wrap-break-word leading-snug">
                      {o.label}
                    </span>
                    {selected && <Check className="w-3.5 h-3.5 shrink-0" />}
                  </li>
                );
              })
            )}
          </ul>,
          getLayerRoot(),
        )}
      {hint && <p className="text-dense-1 text-pb-text-muted mt-1">{hint}</p>}
    </div>
  );
}
